import { Card, Switch, Table, TableColumnsType } from "antd";
import React, { useEffect, useState } from "react";
import { eResultCode } from "../../../../utils/enum";
import useFetch from "../../../../hooks/useFetch";
import { GetDashboardPartnerData } from "../../../../utils/api.constant";
import { format } from "path";
import { current } from "@reduxjs/toolkit";
import DateUtils from "../../../../utils/date";
import { TDropdownOptions } from "../../../../types/config";
import { dateOnlyFormat } from "../../../../utils/constants";

interface DataType {
  servicePartnerId: number;
  servicePartnerName: string;
  productivityPercent: number;
  presentCount: number;
  absentCount: number;
  avgTimeLocal: number;
  avgTimeUpCountry: number;
  avgTimeRemote: number;
}

type PartnerDataTableProps = {
  viewDate: string;
  customerIds: string[];
  servicePartnerIds: string[];
  customerOptions: TDropdownOptions[];
  partnerOptions: TDropdownOptions[];
  onDataFetch: (data: DataType[]) => void;
};

const PartnerDataTable: React.FC<PartnerDataTableProps> = ({
  viewDate,
  customerIds,
  servicePartnerIds,
  customerOptions,
  partnerOptions,
  onDataFetch,
}) => {
  const { post } = useFetch();
  const [isLoading, setIsLoading] = useState(false);
  const [partnerData, setPartnerData] = useState<DataType[]>([]);
  const [selectedDay, setSelectedDay] = useState<number>(1);
  const formatDate = (date: Date) => {
    return date.toISOString().split("T")[0];
  };
  const columns2: TableColumnsType<DataType> = [
    {
      title: "Service Partner",
      dataIndex: "servicePartnerName",
      key: "servicePartnerName",
      fixed: "left",
      width: 250,
      className: "font-medium cell-bordered",
    },
    {
      title: "Average Calls Per Day",
      align: "center",
      children: [
        {
          title: () => <span style={{ color: "#07776C" }}>Productivity</span>,
          dataIndex: "productivityPercent",
          key: "productivityPercent",
          align: "center",
          render: (text: number) => `${text}`,
          className: "cell-bordered",
        },
      ],
    },
    {
      title: "Service Engineer Availability",
      children: [
        {
          title: (
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="8" cy="8" r="8" fill="#0BB68C" />
              <path
                d="M4.31826 13V3.9H8.25726C9.07192 3.9 9.77392 4.03433 10.3633 4.303C10.9526 4.563 11.4076 4.94 11.7283 5.434C12.0489 5.928 12.2093 6.51733 12.2093 7.202C12.2093 7.878 12.0489 8.463 11.7283 8.957C11.4076 9.451 10.9526 9.83233 10.3633 10.101C9.77392 10.361 9.07192 10.491 8.25726 10.491H5.48826L6.42426 9.542V13H4.31826ZM6.42426 9.776L5.48826 8.775H8.14026C8.79026 8.775 9.27559 8.63633 9.59626 8.359C9.91692 8.08167 10.0773 7.696 10.0773 7.202C10.0773 6.69933 9.91692 6.30933 9.59626 6.032C9.27559 5.75467 8.79026 5.616 8.14026 5.616H5.48826L6.42426 4.615V9.776Z"
                fill="white"
              />
            </svg>
          ),
          dataIndex: "presentCount",
          key: "presentCount",
          align: "center",
          render: (text: number) => (
            <span style={{ color: "#2E8B57", fontWeight: "bold" }}>{text}</span>
          ),
        },
        {
          title: (
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="8" cy="8" r="8" fill="#FF6161" />
              <path
                d="M2.90644 13L6.96244 3.9H9.04244L13.1114 13H10.9014L7.57344 4.966H8.40544L5.06444 13H2.90644ZM4.93444 11.05L5.49344 9.451H10.1734L10.7454 11.05H4.93444Z"
                fill="white"
              />
            </svg>
          ),
          dataIndex: "absentCount",
          key: "absentCount",
          align: "center",
          render: (text: number) => (
            <span style={{ color: "red", fontWeight: "bold" }}>{text}</span>
          ),
          className: "cell-bordered",
        },
      ],
    },
    {
      title: "Avg time taken to solve issue",
      children: [
        {
          title: () => <span style={{ color: "#2E8B57" }}>Local (24Hr)</span>,
          dataIndex: "avgTimeLocal",
          key: "avgTimeLocal",
          align: "center",
        },
        {
          title: () => (
            <span style={{ color: "#2E8B57" }}>Up Country (48Hr)</span>
          ),
          dataIndex: "avgTimeUpCountry",
          key: "avgTimeUpCountry",
          align: "center",
        },
        {
          title: () => <span style={{ color: "#2E8B57" }}>Remote (72Hr)</span>,
          dataIndex: "avgTimeRemote",
          key: "avgTimeRemote",
          align: "center",
          className: "cell-bordered",
        },
      ],
    },
  ];

  const handleSwitchChange = (checked: boolean) => {
    if (checked) {
      setSelectedDay(1); // Set to current date
    } else {
      const previousDate = 0;
      setSelectedDay(previousDate);
    }
  };

  const getPartnerData = async () => {
    setIsLoading(true);
    try {
      const payload = {
        data: {
          viewDate: DateUtils.getDateStringWithOutTimezone(
            new Date(viewDate).toDateString()
          ),
          IsAllCustomerRequest: customerIds?.includes("ALL") ? 1 : 0,
          IsAllServicePartnerRequest: servicePartnerIds?.includes("ALL")
            ? 1
            : 0,
          customerId: customerIds?.includes("ALL")
            ? customerOptions
                ?.filter((option) => option.value !== "ALL")
                .map((option) => option.value)
            : customerIds,
          servicePartnerId: servicePartnerIds?.includes("ALL")
            ? partnerOptions
                ?.filter((option) => option.value !== "ALL")
                .map((option) => option.value)
            : servicePartnerIds,
          currentDay: selectedDay,
        },
      };

      const response = await post(GetDashboardPartnerData, payload);
      if (response) {
        const { data, dataResponse } = response;
        const { returnCode } = dataResponse;

        if (returnCode === eResultCode.SUCCESS) {
          const formattedData: DataType[] = data.map((item: any) => ({
            servicePartnerId: item.servicePartnerId,
            servicePartnerName: item.servicePartnerName,
            productivityPercent: item.productivityPercent,
            presentCount: item.presentCount,
            absentCount: item.absentCount,
            avgTimeLocal: item.avgTimeLocal,
            avgTimeUpCountry: item.avgTimeUpCountry,
            avgTimeRemote: item.avgTimeRemote,
          }));
          setPartnerData(formattedData);
          setIsLoading(false);
        }
      }
    } catch (error) {
      console.error("Error fetching partner data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (customerIds?.length && servicePartnerIds?.length) {
      getPartnerData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewDate, JSON.stringify(customerIds), JSON.stringify(servicePartnerIds), selectedDay]);

  useEffect(() => {
    onDataFetch(partnerData);
  }, [partnerData]);

  return (
    <Card
      bordered={false}
      style={{
        borderRadius: "1px",
        maxHeight: "100%",
        height: "100%",
        overflow: "scroll",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-around",
          alignItems: "center",
          marginBottom: "10px",
          marginTop: "10px",
          width: "19%",
        }}
      >
        <p style={{ margin: 0, color: "#07776C" }}>
          {viewDate
            ? DateUtils.format(
                new Date(
                  new Date(viewDate).setDate(new Date(viewDate).getDate() - 1)
                ),
                dateOnlyFormat
              )
            : ""}
        </p>

        <Switch
          defaultChecked
          onChange={handleSwitchChange} // Update switch change handler
        />
        <p style={{ margin: 0, color: "#0EB68C" }}>
          {viewDate ? DateUtils.format(viewDate, dateOnlyFormat) : ""}
        </p>
      </div>
      <Table
        columns={columns2}
        dataSource={partnerData}
        loading={isLoading}
        rowKey="servicePartnerId"
        size="small"
        pagination={false}
        scroll={{ x: "max-content" }}
        sticky
        // bordered
        style={{ maxHeight: "210px" }}
      />
    </Card>
  );
};

export default PartnerDataTable;
